import { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarDays, Clock, Edit3, Plus, AlertCircle, Power, PowerOff, Search, X, MoreHorizontal, Copy, Eye, Archive } from "lucide-react";
import { cn } from "@/lib/utils";
import { Pagination, PaginationInfo } from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { programApi } from "@/lib/api/program";
import { categoryApi } from "@/lib/api";
import { programsV2Api, type ProgramV2, type ProgramsV2Params } from "@/lib/api/v2/programs";
import { useDebounce } from "@/hooks/useDebounce";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DraftsListV2 } from "@/components/programs/DraftsListV2";
import { toast } from "sonner";
import { useProgramStore } from "@/stores/programStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import config from "@/config";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  type DeliveryModeV2,
  getDeliveryModeDisplay,
} from "@/types/program-v2";


// Use the ProgramV2 type from the V2 API
type Program = ProgramV2;

const DELIVERY_MODE_ORDER: DeliveryModeV2[] = ["hybrid", "via-zoom", "on-campus", "online"];

function getProgramModes(program: Program): DeliveryModeV2[] {
  const rawTypes = program.availableProgramTypes ?? [];
  const modes = rawTypes
    .map((pt) => {
      if (typeof pt === "object" && pt !== null && "type" in pt) {
        return (pt as { type?: string }).type;
      }
      return null;
    })
    .filter((type): type is DeliveryModeV2 =>
      DELIVERY_MODE_ORDER.includes(type as DeliveryModeV2)
    );

  return DELIVERY_MODE_ORDER.filter((mode) => modes.includes(mode));
}

type ProgramCategoryOption = {
  _id: string;
  title: string;
  field?: string;
};

const PROGRAMS_PAGE_SIZE = 15;

type NormalizedProgramsPagination = {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

function normalizeProgramsPagination(
  raw: Record<string, unknown> | undefined,
  requestPage: number,
  requestLimit: number,
  programsLength: number
): NormalizedProgramsPagination {
  const currentPage = Math.max(1, Number(raw?.currentPage ?? raw?.page ?? requestPage) || requestPage);
  const limit = Math.max(1, Number(raw?.limit ?? requestLimit) || requestLimit);
  let totalPages = Number(raw?.totalPages ?? raw?.pages ?? 0);
  let totalCount = Number(raw?.totalCount ?? raw?.total ?? 0);
  const hasNextFlag = raw?.hasNextPage;
  const hasPrevFlag = raw?.hasPrevPage;

  if (totalPages < 1) {
    if (totalCount > 0) {
      totalPages = Math.max(1, Math.ceil(totalCount / limit));
    } else if (hasNextFlag === true) {
      totalPages = currentPage + 1;
    } else {
      totalPages = Math.max(1, currentPage);
    }
  }

  if (hasNextFlag === true && totalPages <= currentPage) {
    totalPages = currentPage + 1;
  }

  if (totalCount < 1 && programsLength > 0) {
    totalCount =
      hasNextFlag === true
        ? currentPage * limit + Math.max(programsLength, 1)
        : (currentPage - 1) * limit + programsLength;
  }

  const hasNextPage = typeof hasNextFlag === "boolean" ? hasNextFlag : currentPage < totalPages;
  const hasPrevPage = typeof hasPrevFlag === "boolean" ? hasPrevFlag : currentPage > 1;

  return {
    currentPage,
    totalPages,
    totalCount,
    limit,
    hasNextPage,
    hasPrevPage,
  };
}

// Add debug logging
const DEBUG = false;
const log = (...args: any[]) => {
  if (DEBUG) {
    console.log('[Programs V2]', ...args);
  }
};


const ProgramsV2Content = () => {
  log('Component rendering');

  // State
  const [isFetchingPrograms, setIsFetchingPrograms] = useState(true);
  const [hasLoadedProgramsOnce, setHasLoadedProgramsOnce] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [pagination, setPagination] = useState<NormalizedProgramsPagination | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput.trim(), 500);
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState<ProgramCategoryOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("published");
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showNewProgramDialog, setShowNewProgramDialog] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { reset, draftId, isDraftMode, saveDraft } = useProgramStore();

  useEffect(() => {
    const fromUrl = searchParams.get("categoryId");
    if (fromUrl) {
      setCategoryId(fromUrl);
      setCurrentPage(1);
    }
  }, [searchParams]);

  const handleStatusFilterChange = useCallback((value: 'all' | 'active' | 'inactive') => {
    setStatusFilter(value);
    setCurrentPage(1);
  }, []);

  const handleCategoryFilterChange = useCallback((value: string) => {
    setCategoryId(value === 'all' ? '' : value);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await categoryApi.getCategoriesList();
      if (cancelled) return;
      if (res.success && Array.isArray(res.categories)) {
        setCategories(res.categories);
      } else if (!res.success) {
        toast.error(res.message || 'Failed to load categories');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNewProgram = () => {
    // Check if there's an existing draft being edited
    if (draftId || isDraftMode) {
      setShowNewProgramDialog(true);
    } else {
      // No draft, proceed normally
      reset();
      navigate('/programs-v2/create');
    }
  };

  const handleSaveAndCreateNew = async () => {
    try {
      await saveDraft();
      reset();
      navigate('/programs-v2/create');
    } catch (error) {
      // Error is handled in the store
    } finally {
      setShowNewProgramDialog(false);
    }
  };

  const handleDiscardAndCreateNew = () => {
    reset();
    navigate('/programs-v2/create');
    setShowNewProgramDialog(false);
  };

  // Load data (server-side pagination)
  const loadData = useCallback(async (isRetry = false) => {
    if (isRetry) {
      setIsRetrying(true);
    }
    log('loadData started', { currentPage, statusFilter });
    setIsFetchingPrograms(true);
    setError(null);

    try {
      const params: ProgramsV2Params = {
        page: currentPage,
        limit: PROGRAMS_PAGE_SIZE,
        status: statusFilter === 'all' ? 'all' : statusFilter,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (categoryId) {
        params.categoryId = categoryId;
      }

      const response = await programsV2Api.fetchAllPrograms(params);

      log('Programs V2 response:', response);

      if (response?.success !== true || !response.data?.programs) {
        throw new Error(response?.message || 'Failed to load programs');
      }

      const list = response.data.programs;
      setPrograms(list);

      const rawPagination = response.data.pagination as Record<string, unknown> | undefined;
      setPagination(
        normalizeProgramsPagination(rawPagination, currentPage, PROGRAMS_PAGE_SIZE, list.length)
      );
      setRetryCount(0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred while loading data";
      log('Error in loadData:', err);

      if (err instanceof Error && err.message.includes('Authentication failed')) {
        setError('Your session has expired. Please refresh the page to log in again.');
      } else if (err instanceof Error && err.message.includes('Network error')) {
        setError('Unable to connect to the server. Please check your connection and try again.');
      } else {
        setError(errorMessage);
      }

      setPrograms([]);
      setPagination(null);
    } finally {
      setIsFetchingPrograms(false);
      setHasLoadedProgramsOnce(true);
      setIsRetrying(false);
    }
  }, [currentPage, statusFilter, debouncedSearch, categoryId]);

  // Retry function
  const retryLoadData = useCallback(() => {
    if (retryCount < 3 && !isRetrying) {
      setRetryCount(prev => prev + 1);
      loadData(true);
    }
  }, [retryCount, isRetrying, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (
      !isFetchingPrograms &&
      programs.length === 0 &&
      pagination &&
      currentPage > pagination.totalPages &&
      pagination.totalPages >= 1
    ) {
      setCurrentPage(pagination.totalPages);
    }
  }, [isFetchingPrograms, programs.length, pagination, currentPage]);

  log('Current state:', {
    programsLoading: isFetchingPrograms,
    error,
    programsCount: programs.length,
    currentPage,
    statusFilter,
  });

  return (
    <div className="container mx-auto py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div className="space-y-1.5 sm:space-y-2">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Programs
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
            Build and maintain your catalog: search and filter published programs, turn them active or inactive,
            and keep in-progress work in drafts until you are ready to go live.
          </p>
        </div>
        <Button 
          onClick={handleNewProgram}
          className="w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">New Program</span>
          <span className="sm:hidden">New</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-h-0">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="published" className="text-xs sm:text-sm">Published Programs</TabsTrigger>
          <TabsTrigger value="drafts" className="text-xs sm:text-sm">Drafts</TabsTrigger>
        </TabsList>

        {/* flex-none: default TabsContent uses flex-1 which stretches to fill the main column and leaves blank scroll below */}
        <TabsContent value="published" className="mt-4 sm:mt-6 flex-none outline-none">
          <PublishedPrograms
            programs={programs}
            isInitialLoading={!hasLoadedProgramsOnce && isFetchingPrograms}
            isListRefreshing={hasLoadedProgramsOnce && isFetchingPrograms}
            error={error}
            onRetry={retryLoadData}
            canRetry={retryCount < 3}
            isRetrying={isRetrying}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            categoryId={categoryId}
            onCategoryFilterChange={handleCategoryFilterChange}
            categories={categories}
            hasActiveFilters={
              debouncedSearch.length > 0 ||
              categoryId !== '' ||
              statusFilter !== 'all'
            }
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            pagination={pagination}
            onRefetch={() => loadData(false)}
          />
        </TabsContent>

        <TabsContent value="drafts" className="mt-4 sm:mt-6 flex-none outline-none">
          <DraftsListV2 />
        </TabsContent>
      </Tabs>

      {/* New Program Confirmation Dialog */}
      <AlertDialog open={showNewProgramDialog} onOpenChange={setShowNewProgramDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have an existing draft</AlertDialogTitle>
            <AlertDialogDescription>
              You're currently working on a draft. What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardAndCreateNew}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
            >
              Discard & Create New
            </AlertDialogAction>
            <AlertDialogAction onClick={handleSaveAndCreateNew}>
              Save & Create New
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const PublishedPrograms = ({
  programs,
  isInitialLoading,
  isListRefreshing,
  error,
  onRetry,
  canRetry,
  isRetrying,
  statusFilter,
  onStatusFilterChange,
  searchInput,
  onSearchChange,
  categoryId,
  onCategoryFilterChange,
  categories,
  hasActiveFilters,
  currentPage,
  onPageChange,
  pagination,
  onRefetch,
}: {
  programs: Program[];
  isInitialLoading: boolean;
  isListRefreshing: boolean;
  error: string | null;
  onRetry?: () => void;
  canRetry?: boolean;
  isRetrying?: boolean;
  statusFilter: 'all' | 'active' | 'inactive';
  onStatusFilterChange: (value: 'all' | 'active' | 'inactive') => void;
  searchInput: string;
  onSearchChange: (value: string) => void;
  categoryId: string;
  onCategoryFilterChange: (value: string) => void;
  categories: ProgramCategoryOption[];
  hasActiveFilters: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  pagination: NormalizedProgramsPagination | null;
  onRefetch: () => void;
}) => {
  const [localPrograms, setLocalPrograms] = useState<Program[]>(programs);
  const [selectedPrograms, setSelectedPrograms] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  useEffect(() => {
    setLocalPrograms(programs);
    setSelectedPrograms(new Set());
  }, [programs]);

  const handleStatusChange = (programId: string, newStatus: string) => {
    setLocalPrograms(prev => 
      prev.map(program => 
        program._id === programId 
          ? { ...program, status: newStatus }
          : program
      )
    );
  };

  const handleProgramSelect = (programId: string, checked: boolean) => {
    setSelectedPrograms(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(programId);
      } else {
        newSet.delete(programId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPrograms(new Set(localPrograms.map(p => p._id)));
    } else {
      setSelectedPrograms(new Set());
    }
  };

  const handleBulkStatusUpdate = async (newStatus: 'active' | 'inactive') => {
    if (selectedPrograms.size === 0) {
      toast.error('Please select at least one program');
      return;
    }

    setIsBulkUpdating(true);
    try {
      const selectedProgramIds = Array.from(selectedPrograms);
      const updatePromises = selectedProgramIds.map(programId =>
        programApi.updateProgram(programId, { status: newStatus })
      );

      const results = await Promise.allSettled(updatePromises);
      const successful = results.filter(result => 
        result.status === 'fulfilled' && result.value.success
      ).length;

      if (successful === selectedProgramIds.length) {
        toast.success(`Successfully ${newStatus === 'active' ? 'activated' : 'deactivated'} ${successful} program${successful > 1 ? 's' : ''}`);
        setSelectedPrograms(new Set());
        onRefetch();
      } else {
        toast.error(`Failed to update some programs. ${successful}/${selectedProgramIds.length} updated successfully.`);
      }
    } catch (error: any) {
      console.error('Error in bulk status update:', error);
      toast.error('Failed to update program statuses');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const isAllSelected =
    localPrograms.length > 0 && selectedPrograms.size === localPrograms.length;

  if (isInitialLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} canRetry={canRetry} isRetrying={isRetrying} />;
  }

  if (!localPrograms.length && currentPage === 1 && !hasActiveFilters) {
    return <EmptyState type="published" />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Tabs
        value={statusFilter}
        onValueChange={(v) => onStatusFilterChange(v as "all" | "active" | "inactive")}
        className="w-full gap-3"
      >
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1.5 rounded-lg bg-muted/80 p-1.5 sm:flex sm:h-11 sm:max-w-2xl">
          <TabsTrigger value="all" className="flex-1 px-2 py-2.5 text-xs font-semibold sm:px-4 sm:text-sm">
            All Programs
          </TabsTrigger>
          <TabsTrigger value="active" className="flex-1 px-2 py-2.5 text-xs font-semibold sm:px-4 sm:text-sm">
            Active Only
          </TabsTrigger>
          <TabsTrigger value="inactive" className="flex-1 px-2 py-2.5 text-xs font-semibold sm:px-4 sm:text-sm">
            Inactive Only
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search + filters / bulk in one toolbar */}
      <div className="flex flex-col gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg lg:flex-row lg:items-center lg:justify-between lg:gap-4">
        <div className="relative w-full min-w-0 lg:flex-1 lg:max-w-md xl:max-w-lg">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search title, program ID, or slug…"
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            className={cn("h-10 w-full pl-9 bg-background", searchInput ? "pr-9" : "pr-3")}
            aria-label="Search programs"
          />
          {searchInput ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0.5 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => onSearchChange("")}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between lg:justify-end lg:gap-3 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
            {/* Category filter */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">Category</span>
              <Select
                value={categoryId || 'all'}
                onValueChange={onCategoryFilterChange}
              >
                <SelectTrigger className="w-full sm:w-[200px] bg-background">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selection Controls */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
                disabled={isBulkUpdating}
              />
              <span className="text-xs sm:text-sm font-medium">
                {selectedPrograms.size > 0 
                  ? `${selectedPrograms.size} program${selectedPrograms.size > 1 ? 's' : ''} selected`
                  : 'Select all'
                }
              </span>
            </div>
          </div>

          {selectedPrograms.size > 0 && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkStatusUpdate('active')}
                disabled={isBulkUpdating}
                className="w-full sm:w-auto bg-background"
              >
                <Power className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Activate All</span>
                <span className="sm:hidden">Activate</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkStatusUpdate('inactive')}
                disabled={isBulkUpdating}
                className="w-full sm:w-auto bg-background"
              >
                <PowerOff className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Deactivate All</span>
                <span className="sm:hidden">Deactivate</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Programs List — overlay refresh only here so search/filters stay mounted */}
      <div className="relative min-h-[8rem] rounded-lg">
        {isListRefreshing ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-[1px]"
            aria-busy="true"
            aria-live="polite"
          >
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-xs sm:text-sm">Updating results…</span>
            </div>
          </div>
        ) : null}
        {localPrograms.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm sm:text-base">
              {isListRefreshing
                ? 'Loading…'
                : hasActiveFilters && currentPage === 1
                  ? 'No programs match your search or filters. Try adjusting them.'
                  : 'No programs on this page.'}
            </p>
          </div>
        ) : (
          <div className={`space-y-2 ${isListRefreshing ? 'pointer-events-none opacity-60' : ''}`}>
            {localPrograms.map((program) => (
              <ProgramListItem 
                key={program._id} 
                program={program} 
                onStatusChange={handleStatusChange}
                isSelected={selectedPrograms.has(program._id)}
                onSelect={handleProgramSelect}
                isBulkUpdating={isBulkUpdating}
                onRefetch={onRefetch}
              />
            ))}
          </div>
        )}
      </div>

      {pagination &&
        (pagination.totalPages > 1 ||
          pagination.totalCount > 0 ||
          currentPage > 1) && (
        <div className="flex flex-col gap-4 border-t border-border pt-6 mt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-lg border bg-card/50 px-4 py-3 shadow-sm">
            <PaginationInfo
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalCount}
              itemsPerPage={pagination.limit}
              className="tabular-nums"
            />
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={onPageChange}
                showFirstLast={false}
                maxVisiblePages={7}
                className="justify-start sm:justify-end"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProgramListItem = ({ 
  program, 
  onStatusChange,
  isSelected,
  onSelect,
  isBulkUpdating,
  onRefetch,
}: { 
  program: Program; 
  onStatusChange?: (programId: string, newStatus: string) => void;
  isSelected?: boolean;
  onSelect?: (programId: string, checked: boolean) => void;
  isBulkUpdating?: boolean;
  onRefetch?: () => void;
}) => {
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const handleStatusToggle = async (checked: boolean) => {
    const newStatus = checked ? 'active' : 'inactive';
    
    setIsUpdating(true);
    try {
      const response = await programApi.updateProgram(program._id, {
        status: newStatus
      });

      if (response.success) {
        toast.success(`Program ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        onStatusChange?.(program._id, newStatus);
        onRefetch?.();
      } else {
        toast.error(response.message || 'Failed to update program status');
      }
    } catch (error: any) {
      console.error('Error updating program status:', error);
      toast.error(error.message || 'Failed to update program status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDuplicate = async () => {
    setIsCloning(true);
    try {
      const res = await programsV2Api.cloneProgram(program._id);
      if (!res?.success || !res.data?._id) {
        toast.error(res?.message || 'Failed to duplicate program');
        return;
      }
      toast.success('Program duplicated successfully');
      onRefetch?.();
    } catch (err: any) {
      console.error('Error duplicating program:', err);
      toast.error(err?.message || 'Failed to duplicate program');
    } finally {
      setIsCloning(false);
    }
  };

  const programModes = getProgramModes(program);
  const defaultMode =
    typeof program.defaultProgramType === "object" && program.defaultProgramType?.type
      ? program.defaultProgramType.type
      : undefined;

  return (
    <div className={`${isSelected ? "ring-2 ring-primary bg-primary/5" : "bg-card"} border rounded-lg p-4 hover:bg-muted/50 transition-colors`}>
      <div className="flex items-center justify-between gap-4">
        {/* Left section - Selection, Title, Description */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {onSelect && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelect(program._id, checked as boolean)}
              disabled={isBulkUpdating}
              className="mt-1 flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold truncate">
                {program.title}
              </h3>
              <Badge variant={program.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                {program.status}
              </Badge>
            </div>
            {programModes.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {programModes.map((mode) => (
                  <Badge
                    key={mode}
                    variant="outline"
                    className={cn(
                      "text-xs font-normal",
                      mode === defaultMode && "border-primary/40 bg-primary/5 text-foreground"
                    )}
                  >
                    {getDeliveryModeDisplay(mode)}
                  </Badge>
                ))}
              </div>
            )}
            {program.description ? (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {program.description}
              </p>
            ) : null}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Updated {new Date(program.updatedAt || '').toLocaleDateString()}</span>
              </div>
              {program.category && (
                <div className="flex items-center gap-1">
                  <span>•</span>
                  <span>{program.category.title}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 px-2" disabled={isUpdating || isBulkUpdating || isCloning}>
                {isCloning ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                <span className="hidden sm:inline ml-2">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  const identifier = program.slug || program.programId || program._id;

                  // Prefer student-facing page when configured; otherwise fall back to admin details page
                  if (config.studentAppUrl) {
                    const base = config.studentAppUrl.replace(/\/+$/, "");
                    window.open(`${base}/programs/${identifier}`, "_blank", "noopener,noreferrer");
                    return;
                  }

                  // Admin details route exists; the previous `/programs-v2/:id` route does not.
                  navigate(`/programs/${program._id}`);
                }}
              >
                <Eye className="h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/programs-v2/${program._id}/edit/step1`)}>
                <Edit3 className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDuplicate} disabled={isCloning}>
                <Copy className="h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleStatusToggle(program.status !== 'active')}
                disabled={isUpdating || isBulkUpdating}
              >
                {program.status === 'active' ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                {program.status === 'active' ? 'Hide' : 'Unhide'}
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Archive className="h-4 w-4" />
                Archive / Retrieve (coming soon)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div className="space-y-2">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="bg-card border rounded-lg p-4 animate-pulse">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-4 h-4 bg-muted rounded mt-1"></div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-4 bg-muted rounded w-48"></div>
                <div className="h-5 bg-muted rounded w-16"></div>
              </div>
              <div className="h-3 bg-muted rounded w-full"></div>
              <div className="h-3 bg-muted rounded w-3/4"></div>
              <div className="flex items-center gap-4">
                <div className="h-3 bg-muted rounded w-32"></div>
                <div className="h-3 bg-muted rounded w-24"></div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-6 bg-muted rounded-full"></div>
            <div className="flex gap-1">
              <div className="w-12 h-8 bg-muted rounded"></div>
              <div className="w-8 h-8 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    ))}
    <div className="flex justify-center py-4">
      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading programs...</span>
      </div>
    </div>
  </div>
);

const ErrorState = ({ message, onRetry, canRetry = false, isRetrying = false }: { 
  message: string; 
  onRetry?: () => void; 
  canRetry?: boolean;
  isRetrying?: boolean;
}) => (
  <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center px-4">
    <AlertCircle className="h-8 w-8 sm:h-12 sm:w-12 text-destructive mb-3 sm:mb-4" />
    <h3 className="text-base sm:text-lg font-semibold">Something went wrong</h3>
    <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md">{message}</p>
    {canRetry && onRetry && (
      <Button 
        onClick={onRetry} 
        disabled={isRetrying}
        className="mt-4"
        variant="outline"
      >
        {isRetrying ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Retrying...
          </>
        ) : (
          'Try Again'
        )}
      </Button>
    )}
  </div>
);

const EmptyState = ({ type }: { type: 'published' | 'drafts' }) => (
  <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center px-4">
    <div className="rounded-full bg-muted p-3 sm:p-4 mb-3 sm:mb-4">
      {type === 'published' ? (
        <CalendarDays className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
      ) : (
        <Edit3 className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
      )}
    </div>
    <h3 className="text-base sm:text-lg font-semibold">
      No {type === 'published' ? 'programs' : 'drafts'} yet
    </h3>
    <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md">
      {type === 'published'
        ? 'Get started by creating your first program'
        : 'Create a draft to start working on a new program'}
    </p>
  </div>
);

const ProgramsV2 = () => {
  return (
    <ErrorBoundary>
      <ProgramsV2Content />
    </ErrorBoundary>
  );
};

export default ProgramsV2;
